import classNames from "classnames";
import PropTypes from "prop-types";
import { forwardRef } from "react";

import Flex from "@/components-library/layout/flex";
import classes from "./switch.module.scss";
import Label from "@/components-library/widgets/label";

export const HORIZONTAL = "horizontal";
export const VERTICAL = "vertical";

const Switch = forwardRef(function Switch(
  {
    size,
    orientation,
    isEnabled,
    isChecked,
    labelOn,
    labelOff,
    checkedIcon,
    uncheckedIcon,
    onChange,
    className,
  },
  ref
) {
  const isHorizontal = orientation === HORIZONTAL;

  const handleChange = (event) => {
    event.stopPropagation();
    onChange && onChange(!isChecked);
  };

  return (
    <label
      className={classNames(className, classes.label, classes[orientation], {
        [classes.disabled]: !isEnabled,
      })}
    >
      <input
        ref={ref}
        className={classes.input}
        type="checkbox"
        checked={isChecked}
        onChange={isEnabled ? handleChange : undefined}
        disabled={!isEnabled}
        value={isChecked}
      />

      {(checkedIcon || uncheckedIcon) && (
        <div
          className={classNames(
            classes.icon,
            classes[size],
            classes[orientation]
          )}
        ></div>
      )}
      {isHorizontal && labelOff && (
        <div
          className={classNames(
            classes.label,
            classes.off,
            classes[size],
            classes[orientation]
          )}
        >
          {labelOff}
        </div>
      )}
      <div
        className={classNames(
          classes.switch,
          classes[size],
          classes[orientation],
          {
            [classes.checked]: isChecked,
            [classes.disabled]: !isEnabled,
          }
        )}
      >
        <div
          className={classNames(
            classes.slider,
            classes[size],
            classes[orientation],
            {
              [classes.checked]: isChecked,
              [classes.disabled]: !isEnabled,
            }
          )}
        ></div>
      </div>
      {isHorizontal && labelOn && (
        <Label
          weight="bold"
          tag="h5"
          color="secondary"
          align="start"
          className={classNames(
            classes.label,
            classes.on,
            classes[size],
            classes[orientation],
            {
              [classes.checked]: isChecked,
            }
          )}
        >
          {labelOn}
        </Label>
      )}

      {!isHorizontal && (
        <Flex direction="column" justify="space-around" align="flex-start">
          {labelOff && (
            <div
              className={classNames(classes.label, classes.off, classes[size])}
            >
              {labelOff}
            </div>
          )}
          {labelOn && (
            <div
              className={classNames(classes.label, classes.on, classes[size], {
                [classes.checked]: isChecked,
              })}
            >
              {labelOn}
            </div>
          )}
        </Flex>
      )}
    </label>
  );
});

Switch.sizes = ["xs", "sm", "md", "lg", "xl"];
Switch.orientations = [HORIZONTAL, VERTICAL];

Switch.propTypes = {
  size: PropTypes.oneOf(Switch.sizes).isRequired,
  orientation: PropTypes.oneOf(Switch.orientations).isRequired,
  isEnabled: PropTypes.bool.isRequired,
  isChecked: PropTypes.bool.isRequired,
  labelOn: PropTypes.node,
  labelOff: PropTypes.node,
  checkedIcon: PropTypes.string,
  uncheckedIcon: PropTypes.string,
  onChange: PropTypes.func,
  className: PropTypes.string,
};

Switch.defaultProps = {
  size: "md",
  orientation: HORIZONTAL,
  isEnabled: true,
  isChecked: false,
};

export default Switch;
